import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabase";
import AdminLogin from "./components/AdminLogin";
import AdminServiceList from "./components/AdminServiceList";
import ServiceFormModal from "./components/ServiceFormModal";
import { useServices } from "../../hooks/useServices";
import type { ServiceItem } from "../../mocks/services";

// 認証ゲートの状態。
//  - "loading": getSession 確認中（初期描画でフォームを一瞬出さないため）
//  - "anon":    未ログイン → ログインフォーム
//  - "denied":  ログイン済みだが admin 許可なし
//  - "admin":   admin として確認済み
type AuthState = "loading" | "anon" | "denied" | "admin";

// admin 許可の判定。
// VERIFY against live schema: profiles テーブルに「id（auth.users.id と一致）」と
// 「is_admin: boolean」列がある前提。専用 allowlist テーブルを使う場合はここを差し替える。
async function checkIsAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("profiles")
    .select("is_admin")
    .eq("id", userId)
    .maybeSingle();
  if (error || !data) return false;
  return data.is_admin === true;
}

export default function AdminPage() {
  const { t } = useTranslation("admin");
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ServiceItem | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: "ok" | "error" } | null>(null);
  const { services, addService, updateService, deleteService } = useServices();
  const navigate = useNavigate();

  // 現在のセッションを取得し、admin 許可まで確認してゲート状態を更新する。
  const resolveAuth = useCallback(async () => {
    const { data, error } = await supabase.auth.getSession();
    const session = data?.session ?? null;
    if (error || !session) {
      setAuthState("anon");
      return;
    }
    const ok = await checkIsAdmin(session.user.id);
    setAuthState(ok ? "admin" : "denied");
  }, []);

  useEffect(() => {
    resolveAuth();
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      resolveAuth();
    });
    return () => sub.subscription.unsubscribe();
  }, [resolveAuth]);

  const showToast = (msg: string, kind: "ok" | "error" = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowModal(true);
  };

  const handleEdit = (item: ServiceItem) => {
    setEditing(item);
    setShowModal(true);
  };

  const handleSubmit = async (data: Omit<ServiceItem, "id">) => {
    if (editing) {
      const { error } = await updateService(editing.id, data);
      if (error) {
        showToast(t("toast.updateFailed"), "error");
      } else {
        showToast(t("toast.updated"));
      }
    } else {
      const { error } = await addService(data);
      if (error) {
        showToast(t("toast.addFailed"), "error");
      } else {
        showToast(t("toast.added"));
      }
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await deleteService(id);
    if (error) {
      showToast(t("toast.deleteFailed"), "error");
    } else {
      showToast(t("toast.deleted"));
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAuthState("anon");
  };

  if (authState === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#FAF5E8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'Noto Sans JP', sans-serif",
          color: "#2C5F4F",
          fontSize: 14,
        }}
      >
        {t("loading")}
      </div>
    );
  }

  if (authState === "anon") {
    return <AdminLogin onLogin={resolveAuth} />;
  }

  if (authState === "denied") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#FAF5E8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: 20,
            padding: "48px 40px",
            width: "100%",
            maxWidth: 400,
            border: "1px solid rgba(44,95,79,0.1)",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 700,
              fontSize: 18,
              color: "#1A1A1A",
              marginBottom: 8,
            }}
          >
            {t("deniedTitle")}
          </p>
          <p
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              fontSize: 13,
              color: "#888",
              marginBottom: 24,
            }}
          >
            {t("deniedBody")}
          </p>
          <button
            type="button"
            onClick={handleLogout}
            className="cursor-pointer whitespace-nowrap"
            style={{
              padding: "10px 24px",
              borderRadius: 10,
              border: "1.5px solid rgba(44,95,79,0.2)",
              background: "transparent",
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 600,
              fontSize: 13,
              color: "#555555",
            }}
          >
            {t("logout")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#FAF5E8" }}>
      {/* Header */}
      <header
        style={{
          background: "#ffffff",
          borderBottom: "1px solid rgba(44,95,79,0.1)",
          padding: "0 32px",
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="cursor-pointer flex items-center justify-center w-8 h-8"
            style={{
              borderRadius: 8,
              border: "none",
              background: "rgba(44,95,79,0.07)",
              color: "#2C5F4F",
            }}
            title={t("backToHome")}
          >
            <i className="ri-arrow-left-line" style={{ fontSize: 16 }} />
          </button>
          <div>
            <span
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.12em",
                color: "#2C5F4F",
                textTransform: "uppercase",
              }}
            >
              {t("brandLabel")}
            </span>
            <p
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                color: "#1A1A1A",
                lineHeight: 1.2,
              }}
            >
              {t("title")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="cursor-pointer whitespace-nowrap flex items-center gap-2"
          style={{
            padding: "8px 16px",
            borderRadius: 9,
            border: "1.5px solid rgba(44,95,79,0.15)",
            background: "transparent",
            fontFamily: "'Noto Sans JP', sans-serif",
            fontWeight: 600,
            fontSize: 13,
            color: "#555555",
          }}
        >
          <i className="ri-logout-box-line" style={{ fontSize: 14 }} />
          {t("logout")}
        </button>
      </header>

      {/* Body */}
      <main style={{ maxWidth: 760, margin: "0 auto", padding: "40px 24px 80px" }}>
        {/* Stats bar */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: 14,
            padding: "20px 24px",
            marginBottom: 28,
            border: "1px solid rgba(44,95,79,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#2C5F4F", textTransform: "uppercase" }}>
              {t("publishedServices")}
            </p>
            <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontWeight: 800, fontSize: 28, color: "#1A1A1A", lineHeight: 1.2 }}>
              {services.length}
              <span style={{ fontSize: 14, fontWeight: 500, color: "#888", marginLeft: 6 }}>{t("unit")}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            className="cursor-pointer whitespace-nowrap flex items-center gap-2"
            style={{
              padding: "11px 22px",
              borderRadius: 11,
              border: "none",
              background: "#2C5F4F",
              fontFamily: "'Noto Sans JP', sans-serif",
              fontWeight: 700,
              fontSize: 14,
              color: "#ffffff",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = "0.85")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = "1")}
          >
            <i className="ri-add-line" style={{ fontSize: 17 }} />
            {t("addNew")}
          </button>
        </div>

        {/* Service list */}
        <AdminServiceList
          services={services}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </main>

      {/* Modal */}
      {showModal && (
        <ServiceFormModal
          editing={editing}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            background: toast.kind === "error" ? "#dc2626" : "#2C5F4F",
            color: "#ffffff",
            padding: "12px 24px",
            borderRadius: 12,
            fontFamily: "'Noto Sans JP', sans-serif",
            fontWeight: 600,
            fontSize: 14,
            zIndex: 2000,
            whiteSpace: "nowrap",
          }}
        >
          <i className={toast.kind === "error" ? "ri-error-warning-line" : "ri-check-line"} style={{ marginRight: 8 }} />
          {toast.msg}
        </div>
      )}
    </div>
  );
}
